import {
  Badge,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@izicvs/ui";
import { t } from "@lingui/macro";
import { List, MagnifyingGlass, SquaresFour } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { ThemeSwitch } from "@/client/components/theme-switch";

import { NotificationsBell } from "../_components/notifications";
import { EmptyState, NoResults } from "./_components/empty-state";
import { NewResumeButton, NewResumeFab } from "./_components/new-resume";
import type { ResumeSort } from "./_hooks/use-resumes-view";
import { useResumesView } from "./_hooks/use-resumes-view";
import { GridView } from "./_layouts/grid";
import { ListView } from "./_layouts/list";

type Layout = "grid" | "list";

export const ResumesPage = () => {
  const [layout, setLayout] = useState<Layout>("grid");
  const { resumes, total, loading, search, setSearch, sort, setSort } = useResumesView();

  const isEmpty = !loading && total === 0;
  const isNoResults = !loading && total > 0 && resumes.length === 0;

  return (
    <>
      <Helmet>
        <title>
          {t`Resumes`} - {t`Izicvs`}
        </title>
      </Helmet>

      {/* Header: title + count, with the utility cluster and the desktop CTA. */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-x-3">
          <motion.h1
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-3xl font-bold tracking-tight lg:text-4xl"
          >
            {t`My resumes`}
          </motion.h1>
          {total > 0 && (
            <Badge variant="secondary" className="rounded-full px-2.5">
              {total}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-x-3">
          <NotificationsBell />
          <ThemeSwitch />
          <NewResumeButton className="hidden sm:flex" />
        </div>
      </div>

      {isEmpty ? (
        <EmptyState />
      ) : (
        <Tabs
          value={layout}
          className="space-y-4"
          onValueChange={(value) => {
            setLayout(value as Layout);
          }}
        >
          {/* Mobile: a clear, labelled create button (the FAB alone is easy to miss). */}
          <NewResumeButton className="flex w-full sm:hidden" />

          {/* Toolbar: search on the left, sort + view toggle on the right. */}
          <div className="flex items-center justify-between gap-3">
            <div className="relative w-full max-w-xs">
              <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 opacity-50" />
              <Input
                value={search}
                placeholder={t`Search resumes`}
                className="pl-9"
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </div>

            <div className="flex shrink-0 items-center gap-x-2">
              <Select
                value={sort}
                onValueChange={(value) => {
                  setSort(value as ResumeSort);
                }}
              >
                <SelectTrigger className="hidden w-48 whitespace-nowrap sm:flex">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">{t`Recently updated`}</SelectItem>
                  <SelectItem value="alpha">{t`Alphabetical`}</SelectItem>
                </SelectContent>
              </Select>

              <TabsList>
                <TabsTrigger value="grid" className="size-8 p-0 sm:h-8 sm:w-auto sm:px-4">
                  <SquaresFour />
                  <span className="ml-2 hidden sm:block">{t`Grid`}</span>
                </TabsTrigger>
                <TabsTrigger value="list" className="size-8 p-0 sm:h-8 sm:w-auto sm:px-4">
                  <List />
                  <span className="ml-2 hidden sm:block">{t`List`}</span>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          {isNoResults ? (
            <NoResults
              query={search}
              onClear={() => {
                setSearch("");
              }}
            />
          ) : (
            <ScrollArea
              allowOverflow
              className="h-[calc(100vh-210px)] overflow-visible lg:h-[calc(100vh-160px)]"
            >
              <TabsContent value="grid">
                <GridView resumes={resumes} loading={loading} />
              </TabsContent>
              <TabsContent value="list">
                <ListView resumes={resumes} loading={loading} />
              </TabsContent>
            </ScrollArea>
          )}
        </Tabs>
      )}

      {/* Mobile: thumb-reachable create button. */}
      <NewResumeFab className="sm:hidden" />
    </>
  );
};
